/* ==========================================================================
   The SoArt — Mobile Sticky Purchase Bar (controller)
   soart-sticky-bar.js
   --------------------------------------------------------------------------
   An enhancement that adds NO business logic:

     • Visibility — an IntersectionObserver on the native purchase section
       (Horizon's <add-to-cart-component>) shows the bar once that section has
       scrolled ABOVE the viewport, and hides it when it returns.

     • Add to cart — the bar's button is a PROXY. Clicking it forwards to the
       real Horizon Add-to-Cart button, so the existing capture-phase
       interception (soart-modal.js) opens the SAME delivery modal, and the
       SAME native product form is submitted. No duplicate form, no duplicate
       product logic, no custom AJAX.

     • State mirroring — the proxy reflects the real button's disabled /
       sold-out state, and (optionally) mirrors Horizon's on-page price on
       variant change via the data-soart-price-source selector.

   Only global used: window.SOART (namespace shared with the rest of the kit).
   ========================================================================== */

(function () {
  'use strict';

  var SOART = (window.SOART = window.SOART || {});

  if (SOART.StickyBar) return; /* single definition */

  var TRIGGER_SELECTOR = '[data-soart-delivery-trigger]';

  function StickyBarController(barEl) {
    this.bar = barEl;
    this.proxyBtn = barEl.querySelector('[data-soart-sticky-trigger]');
    this.priceEl = barEl.querySelector('[data-soart-sticky-price]');
    this.realBtn = document.querySelector(TRIGGER_SELECTOR);
    this._observer = null;
    this._mutation = null;
    this._priceMutation = null;
  }

  StickyBarController.prototype.init = function () {
    if (!this.bar || !this.proxyBtn || !this.realBtn) return;

    /* The section whose visibility drives the bar: the native purchase block. */
    this.section =
      this.realBtn.closest('add-to-cart-component') ||
      this.realBtn.closest('form') ||
      this.realBtn;

    this._bindProxy();
    this._syncDisabled();
    this._observeDisabled();
    this._observeVisibility();
    this._bindPriceMirror();
  };

  /* ---- Proxy click → real Horizon button (reuses interception + modal) --- */
  StickyBarController.prototype._bindProxy = function () {
    var self = this;
    this.proxyBtn.addEventListener('click', function (event) {
      event.preventDefault();
      if (self._isRealDisabled()) return;
      /* Fire the genuine button. The document capture listener in
         soart-modal.js intercepts it exactly as a direct tap would, opening the
         modal with the REAL button (and thus the real product form) as the
         trigger. The sticky bar never touches the cart itself. */
      self.realBtn.click();
    });
  };

  /* ---- Disabled / sold-out mirroring ----------------------------------- */
  StickyBarController.prototype._isRealDisabled = function () {
    return (
      this.realBtn.disabled ||
      this.realBtn.getAttribute('aria-disabled') === 'true'
    );
  };

  StickyBarController.prototype._syncDisabled = function () {
    var disabled = this._isRealDisabled();
    if (disabled) {
      this.proxyBtn.setAttribute('disabled', '');
    } else {
      this.proxyBtn.removeAttribute('disabled');
    }
  };

  StickyBarController.prototype._observeDisabled = function () {
    if (typeof MutationObserver === 'undefined') return;
    var self = this;
    this._mutation = new MutationObserver(function () {
      self._syncDisabled();
    });
    this._mutation.observe(this.realBtn, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled']
    });
  };

  /* ---- Visibility: show once the section has scrolled above the viewport - */
  StickyBarController.prototype._show = function () {
    if (this.bar.classList.contains('is-visible')) return;
    this.bar.classList.add('is-visible');
    this.bar.setAttribute('aria-hidden', 'false');
    this.proxyBtn.removeAttribute('tabindex');
  };

  StickyBarController.prototype._hide = function () {
    if (!this.bar.classList.contains('is-visible')) return;
    this.bar.classList.remove('is-visible');
    this.bar.setAttribute('aria-hidden', 'true');
    this.proxyBtn.setAttribute('tabindex', '-1');
  };

  StickyBarController.prototype._observeVisibility = function () {
    var self = this;

    if (typeof IntersectionObserver === 'undefined') {
      /* Very old browser fallback: leave the bar hidden (native button works). */
      return;
    }

    this._observer = new IntersectionObserver(
      function (entries) {
        var entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          /* Native purchase section is on screen → no need for the bar. */
          self._hide();
        } else if (entry.boundingClientRect.top < 0) {
          /* Section has scrolled ABOVE the viewport → surface the bar. */
          self._show();
        } else {
          /* Section is still BELOW the fold (not yet reached) → stay hidden. */
          self._hide();
        }
      },
      { threshold: 0 }
    );

    this._observer.observe(this.section);
  };

  /* ---- Optional price mirroring (reflects Horizon's own price node) ------ */
  StickyBarController.prototype._bindPriceMirror = function () {
    if (typeof MutationObserver === 'undefined') return;
    var selector = this.bar.getAttribute('data-soart-price-source');
    if (!selector || !this.priceEl) return;

    var source = document.querySelector(selector);
    if (!source) return;

    var self = this;
    var apply = function () {
      var text = (source.textContent || '').trim();
      if (text) self.priceEl.textContent = text;
    };
    apply();
    this._priceMutation = new MutationObserver(apply);
    this._priceMutation.observe(source, {
      childList: true,
      characterData: true,
      subtree: true
    });
  };

  /* ---- Bootstrap -------------------------------------------------------- */
  function init() {
    var bars = document.querySelectorAll('[data-soart-sticky-bar]');
    Array.prototype.forEach.call(bars, function (bar) {
      if (bar._soartStickyInit) return;
      bar._soartStickyInit = true;
      new StickyBarController(bar).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  SOART.StickyBar = { init: init, Controller: StickyBarController };
})();